// チャートの点・柱のクリックで詳細ページへ遷移するとき、ドラッグ(ズームの範囲選択)では遷移しないようにする。
//
// ApexCharts 7.9.1 は、ズームのドラッグの mouseup でもマーカーの markerClick を発火し(tooltip/Intersect.js)、
// 柱などの path の dataPointSelection は mousedown で発火する(Graphics.js の pathMouseDown)。
// どちらもドラッグかどうかを見ていないので、押した位置と離した位置の差をこちらで見て判定する。
// ApexCharts の内部フラグや発火順には依存しない。
//
// apex_chart_controller.js は ChartClickGuard を1つ持ち、
// - チャートの要素の capture 相の mousedown / touchstart で handlePointerStart(event) を呼ぶ
// - 遷移したいときは requestNavigation(event, navigate) を呼び、遷移してよいときだけ navigate() が呼ばれる
// - disconnect とチャートの作り直しで reset() を呼ぶ

// ApexCharts がズームとみなすドラッグ量(ZoomPanSelection.js の「dx > 10 || dy > 10」)に合わせ、軸ごとに判定する。
// Math.hypot だと斜めの小さなクリック(dx=8, dy=8 など)を誤って止めるので使わない
export const DRAG_CLICK_THRESHOLD_PX = 10

// タッチのあとにブラウザが合成する mouse イベント(mousedown / mouseup / click)を見分ける時間
export const SYNTHETIC_MOUSE_WINDOW_MS = 600

// mouse / touches / changedTouches の位置を { x, y } にそろえる。位置が取れなければ null
export function getPointerPosition(event) {
  if (!event) return null

  const touch = event.touches?.[0] || event.changedTouches?.[0]
  const source = touch || event
  const x = source.clientX
  const y = source.clientY
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null

  return { x, y }
}

// 開始位置から終了位置までが、どちらかの軸で閾値を超えて動いていればドラッグ。片方でも無ければドラッグではない
export function isDragGesture(start, end, threshold = DRAG_CLICK_THRESHOLD_PX) {
  if (!start || !end) return false

  return Math.abs(end.x - start.x) > threshold || Math.abs(end.y - start.y) > threshold
}

const eventKind = (event) => {
  const type = typeof event?.type === "string" ? event.type : ""
  if (type.startsWith("touch")) return "touch"
  if (type.startsWith("mouse") || type === "click") return "mouse"
  return null
}

const isStartEvent = (event) => event?.type === "mousedown" || event?.type === "touchstart"

// 右クリック・中クリック(button が 1 以上)は、開始位置にも遷移にも使わない
const isSecondaryButton = (event) => eventKind(event) === "mouse" && Number(event.button) > 0

const defaultNow = () => Date.now()
const defaultDocument = () => (typeof document !== "undefined" ? document : null)
const defaultCreateAbortController = () => new AbortController()

export class ChartClickGuard {
  // doc: document(テストでは偽物を渡す)。now: 現在時刻(ms)。createAbortController: AbortController を作る関数
  constructor({
    doc = defaultDocument(),
    now = defaultNow,
    threshold = DRAG_CLICK_THRESHOLD_PX,
    syntheticMouseWindowMs = SYNTHETIC_MOUSE_WINDOW_MS,
    createAbortController = defaultCreateAbortController
  } = {}) {
    this.doc = doc
    this.now = now
    this.threshold = threshold
    this.syntheticMouseWindowMs = syntheticMouseWindowMs
    this.createAbortController = createAbortController
    this.lastTouchEndAt = null
    this.gesture = null
    this.finished = null
    this.abortController = null
  }

  // 最後の touchend から syntheticMouseWindowMs 以内の mouse 系イベントは、タッチの合成イベントとみなす
  isSyntheticMouseEvent(event) {
    if (eventKind(event) !== "mouse") return false
    if (this.lastTouchEndAt === null) return false

    return this.now() - this.lastTouchEndAt <= this.syntheticMouseWindowMs
  }

  // チャートの要素の capture 相の mousedown / touchstart。前のジェスチャーを捨てて、開始位置を記録する
  handlePointerStart(event) {
    const kind = eventKind(event)
    if (!kind) return
    if (isSecondaryButton(event)) return
    if (this.isSyntheticMouseEvent(event)) return

    this.reset()

    const start = getPointerPosition(event)
    if (!start) return

    this.gesture = { kind, start, pending: null, navigated: false }
    this.listenForGestureEnd(kind)
  }

  listenForGestureEnd(kind) {
    if (!this.doc?.addEventListener) return

    const controller = this.createAbortController()
    this.abortController = controller
    const options = { signal: controller.signal }

    if (kind === "touch") {
      this.doc.addEventListener("touchend", (event) => this.handleGestureEnd(event), options)
      this.doc.addEventListener("touchcancel", () => this.handleGestureCancel(), options)
    } else {
      this.doc.addEventListener("mouseup", (event) => this.handleGestureEnd(event), options)
    }
  }

  // document の mouseup / touchend。保留中の遷移があれば、ここで1回だけ判定する
  handleGestureEnd(event) {
    // 左ボタンのジェスチャー中に右・中ボタンを離しても、判定しない
    if (isSecondaryButton(event)) return

    if (eventKind(event) === "touch") {
      this.lastTouchEndAt = this.now()
    }

    const gesture = this.gesture
    this.abortListeners()
    this.gesture = null
    if (!gesture) return

    const isDrag = isDragGesture(gesture.start, getPointerPosition(event), this.threshold)
    let navigated = gesture.navigated
    if (gesture.pending && !isDrag && !navigated) {
      gesture.pending()
      navigated = true
    }

    // 同じ mouseup で markerClick がこのあとに来たとき(発火順が逆のとき)に、もう一度判定するための結果。
    // タッチの合成 mouse イベントでの遷移もこの結果で判定する
    this.finished = { kind: gesture.kind, event, isDrag, navigated }
  }

  handleGestureCancel() {
    this.reset()
  }

  // 遷移してよいときは navigate() を呼ぶ。戻り値は "navigated" | "pending" | "skipped"
  requestNavigation(event, navigate) {
    const kind = eventKind(event)

    // キーボードなど、ポインター以外が起点なら従来どおり遷移する(開始位置が無くても遷移するのはこの場合だけ)
    if (!kind) {
      navigate()
      return "navigated"
    }

    if (isSecondaryButton(event)) return "skipped"

    if (this.isSyntheticMouseEvent(event)) {
      return this.navigateByFinishedTouch(navigate)
    }

    if (this.finished && this.finished.event === event) {
      return this.navigateByFinished(navigate)
    }

    const gesture = this.gesture
    // mouse / touch 由来なのに、このチャートに開始位置が無いときは遷移しない。
    // 別のチャートなどでドラッグを始めて、このチャートの上で離したとき(markerClick が発火する)
    if (!gesture) return "skipped"

    if (gesture.navigated) return "skipped"

    if (isStartEvent(event)) {
      // mousedown / touchstart 由来(柱の dataPointSelection)は、離したときまで待つ
      if (!gesture.pending) gesture.pending = navigate
      return "pending"
    }

    // mouseup / touchend 由来(markerClick)。保留中の遷移があれば、保留側に任せる
    if (gesture.pending) return "skipped"

    // ここで片付けると document の touchend は届かないので、合成 mouse イベントの判定用にここで記録する
    if (event.type === "touchend") this.lastTouchEndAt = this.now()

    const isDrag = isDragGesture(gesture.start, getPointerPosition(event), this.threshold)
    // markerClick だけで終わるジェスチャーは、ここで状態を片付ける
    this.reset()
    this.finished = { kind, event, isDrag, navigated: !isDrag }
    if (isDrag) return "skipped"

    navigate()
    return "navigated"
  }

  navigateByFinished(navigate) {
    const finished = this.finished
    if (finished.isDrag || finished.navigated) return "skipped"

    finished.navigated = true
    navigate()
    return "navigated"
  }

  // タッチのあとの合成 mouse イベントは、開始位置にも判定にも使わず、タッチの判定結果で遷移する(1回だけ)
  navigateByFinishedTouch(navigate) {
    const finished = this.finished
    if (!finished || finished.kind !== "touch") return "skipped"

    return this.navigateByFinished(navigate)
  }

  abortListeners() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  // 保留中の遷移と開始位置を捨て、document 側のリスナーを外す(disconnect・作り直し・touchcancel・新しいジェスチャー)
  reset() {
    this.abortListeners()
    this.gesture = null
    this.finished = null
  }
}
